import {HttpClient} from '@angular/common/http';
import {inject, Injectable} from '@angular/core';
import {BehaviorSubject, distinctUntilChanged, interval, Observable, of, shareReplay, switchMap, take} from 'rxjs';
import {catchError, map, startWith, tap} from 'rxjs/operators';
import {environment} from '~local/environments/environment';
import {AuthenticationServiceBase} from '~shared/types/authentication.types';
import {Router} from '@angular/router';

export type LoginContext = {
    email: string;
    password: string;
    remember?: boolean;
    deviceInfo?: any;
};

export type AuthStatusResponseLoggedIn = {
    loggedIn: true,
    permissions: any[],
    roles: any[],
    restricted: any[],
    source: string,
};

type AuthStatusResponseLoggedOut = {
    loggedIn: false,
};

type AuthStatusResponse = AuthStatusResponseLoggedIn | AuthStatusResponseLoggedOut;

export type Credentials = {
    // Customize received credentials here
    email: string;
    token: string;
    expired?: boolean;
};

export function isCredentials($: any): $ is Credentials {
    return typeof $ === 'object'
        && $ !== null
        && typeof $.email === 'string'
        && typeof $.token === 'string';
}


/**
 * Provides a base for authentication workflow.
 * The login/logout methods should be replaced with proper implementation.
 */
@Injectable()
export class AuthenticationService extends AuthenticationServiceBase {
    protected readonly httpClient = inject(HttpClient);
    protected readonly router = inject(Router);


    public readonly credentials$: Observable<Credentials | null>;
    public readonly status$: Observable<AuthStatusResponseLoggedIn | null>;

    protected readonly credentialsSource$: BehaviorSubject<Credentials | null>;

    public constructor(
    ) {
        super();
        const savedCredentials = sessionStorage.getItem(environment.credentialsKey);
        try {
            const initialCredentialsCandidate = JSON.parse(savedCredentials);
            this.credentialsSource$ = new BehaviorSubject<Credentials | null>(isCredentials(initialCredentialsCandidate) ? initialCredentialsCandidate : null);
        } catch ($err) {
            this.credentialsSource$ = new BehaviorSubject<Credentials | null>(null);
        }

        this.credentials$ = this.credentialsSource$.pipe(
            switchMap($storedCredentials => {
                if (isCredentials($storedCredentials)) {
                    return interval(environment.userIdleConfig.ping * 1000).pipe(
                        switchMap(() => {
                            return this.requestCredentials$();
                        }),
                        // TODO: Don't like this but not sure how to make better implementation
                        tap($remoteCredentials => {
                            // Stopping in case of error
                            if ($remoteCredentials === null) {
                                this.credentialsSource$.next(null);
                            }
                        }),
                        startWith($storedCredentials),
                    );
                }
                return of(null);
            }),
            // TODO: This could be optimized, rewrites already existing data on start.
            tap($credentials => {
                if ($credentials) {
                    sessionStorage.setItem(environment.credentialsKey, JSON.stringify($credentials));
                } else {
                    sessionStorage.removeItem(environment.credentialsKey);
                }
            }),
            shareReplay(1),
        );

        this.status$ = this.credentials$.pipe(
            map($credentials => $credentials !== null),
            distinctUntilChanged(),
            switchMap($hasCredentials => {
                if ($hasCredentials) {
                    return this.requestStatus$().pipe(
                        map($statusResponse => {
                            if ($statusResponse.loggedIn) {
                                return $statusResponse;
                            }
                            return null;
                        })
                    );
                }
                return of(null);
            }),
            shareReplay(1),
        );

        this.isAuthorized$ = this.credentials$.pipe(
            switchMap($credentials => {
                if ($credentials) {
                    return this.status$.pipe(
                        map($status => {
                            if ($status) {
                                // When status is available to application -> we can return true user status.
                                return $status.loggedIn;
                            }
                            // We are assuming that user is logged in when credentials do exist.
                            return true;
                        }),
                    );
                }
                return of(false);
            }),
            distinctUntilChanged(),
        );
    }

    /**
     * Authenticates the user.
     * @param $context The login parameters.
     * @return The user credentials.
     */
    public requestLogin$($context: LoginContext): Observable<Credentials> {
        const body = {
            email: $context.email,
            password: $context.password,
            deviceInfo: $context.deviceInfo
        };

        return this.httpClient.post<{token: string, authorizeViaEmail: boolean}>('/auth/login', body)
            .pipe(
                take(1),
                map($response => {
                    return <Credentials>{
                        email: $context.email,
                        token: $response.token,
                    };
                }),
                tap($credentials => {
                    this.credentialsSource$.next($credentials);
                }),
            );
    }

    /**
     * Logs out the user and clear credentials.
     * @return True if the user was logged out successfully.
     */
    public doLogout$(): Observable<void> {
        return this.requestLogout$().pipe(
            catchError(() => of(undefined)),
            tap(() => {
                this.credentialsSource$.next(null);
            }),
        );
    }

    public resendConfirmation$($email: string): Observable<{ status: 'success' }> {
        return this.httpClient.post<{ status: 'success' }>('/auth/resend', {email: $email});
    }

    public forgotPassword($data: {email: string}): Observable<null> {
       return this.httpClient.post<null>('/auth/forgot-password', $data);
    }

    public forceLogout(): void {
        this.credentialsSource$.next(null);
        this.router.navigateByUrl('/login');
    }

    public resetPassword($data: {password: string, code: string}): Observable<null> {
        return this.httpClient.post<null>('/auth/reset-password', $data);
    }
    protected requestCredentials$(): Observable<Credentials | null> {
        return this.httpClient.post<Credentials>('/auth/refreshToken', {}).pipe(
            take(1),
            catchError(() => {
                return of(null);
            }),
        );
    }

    protected requestLogout$(): Observable<void> {
        return this.httpClient.get<void>('/auth/logout').pipe(
            catchError(() => of(undefined)),
        );
    }

    protected requestStatus$(): Observable<AuthStatusResponse> {
        return this.httpClient.get<AuthStatusResponse>('/auth/status').pipe(
            catchError(() => {
                return of(<AuthStatusResponseLoggedOut>{loggedIn: false});
            }),
        );
    }
}
